import { createAsyncThunk } from "@reduxjs/toolkit";
import client, {
  ApiError,
  AsyncThunkConfig,
  isAxiosError,
} from "../../api/axiosConfig";
import { dispatchNotification } from "../../utils/redux";
import { ExportResultFilters, FileData } from "../../api/psychologicalLabApi";
import { exportResultsURL } from "../../utils/paths";
import { getTimezoneOffset } from "../../utils/timezone";
import {
  ExportFitersData,
  ExportFitersRequestArgs,
} from "../../api/exportResultsApi";

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ExportFitersData;
}

interface FetchFilesApiResponse {
  results: {
    files: FileData[];
  };
}

export const loadExportFiltersData = createAsyncThunk<
  ExportFitersData,
  ExportFitersRequestArgs,
  AsyncThunkConfig
>("selectedPsychLab/loadExportFiltersData/get", async (args, thunkAPI) => {
  try {
    const response = await client.request<ApiResponse>({
      url: `${exportResultsURL}filters/`,
      params: { lab: args.labId },
    });

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message, true);

      return thunkAPI.rejectWithValue(extractedError);
    }
    dispatchNotification("error");
    throw err;
  }
});

export const fetchExportResults = createAsyncThunk<
  void,
  ExportResultFilters,
  AsyncThunkConfig
>(
  "selectedPsychLab/fetchExportResults",
  async (exportResultFilters, thunkAPI) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await client.post<{ message: string }>(
        `${exportResultsURL}export/`,
        exportResultFilters,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Timezone-Offset": getTimezoneOffset().toString(),
          },
        }
      );

      return;
    } catch (err) {
      if (isAxiosError(err) && err.response?.data) {
        const errorData = err.response.data as ApiError;
        const errorMessage =
          errorData.message || "Wystąpił nieznany błąd podczas eksportu.";

        dispatchNotification("error", errorMessage, true);

        return thunkAPI.rejectWithValue(errorData);
      }

      throw err;
    }
  }
);

export const fetchFiles = createAsyncThunk<
  FileData[],
  boolean | undefined,
  AsyncThunkConfig
>("selectedPsychLab/fetchFiles", async (fromAdmin, thunkAPI) => {
  try {
    const params: Record<string, string> = {};

    if (fromAdmin) {
      // snake_case matches backend query param
      // eslint-disable-next-line camelcase -- API query key
      params.from_admin = "true";
    }
    const response = await client.get<FetchFilesApiResponse>(
      `${exportResultsURL}fetchfiles/`,
      { params }
    );

    return response.data.results.files;
  } catch (err) {
    let errorMessage = "Wystąpił błąd podczas pobierania listy plików.";
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (isAxiosError(err) && err.response?.data) {
      apiError = err.response.data as ApiError;
      errorMessage = apiError.message || errorMessage;
      dispatchNotification("error", errorMessage, true);

      return thunkAPI.rejectWithValue(apiError);
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});

export const deleteExportFile = createAsyncThunk<
  string,
  string,
  AsyncThunkConfig
>("selectedPsychLab/deleteExportFile", async (filename, thunkAPI) => {
  try {
    await client.delete(
      `${exportResultsURL}delete/${encodeURIComponent(filename)}/`
    );

    return filename;
  } catch (err) {
    let errorMessage = `Wystąpił błąd podczas usuwania pliku: ${filename}.`;
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (isAxiosError(err) && err.response?.data) {
      try {
        if (
          typeof err.response.data === "object" &&
          !(err.response.data instanceof Blob)
        ) {
          apiError = err.response.data as ApiError;
          errorMessage = apiError.message || errorMessage;
        } else if (
          err.response.data instanceof Blob &&
          err.response.data.type === "application/json"
        ) {
          const errorJson = JSON.parse(
            await err.response.data.text()
          ) as ApiError;

          apiError = errorJson;
          errorMessage = apiError.message || errorMessage;
        }
      } catch {
        // Error processing Axios error data
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});

export const downloadExportFile = createAsyncThunk<
  void,
  string,
  AsyncThunkConfig
>("selectedPsychLab/downloadExportFile", async (filename, thunkAPI) => {
  try {
    const response = await client.get(
      `${exportResultsURL}download/${encodeURIComponent(filename)}/`,
      {
        responseType: "blob",
      }
    );

    if (typeof window !== "undefined") {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = /^(https?:\/\/|blob:)/.test(url) ? url : "#";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return;
  } catch (err) {
    let errorMessage = `Wystąpił błąd podczas pobierania pliku: ${filename}.`;
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (
      isAxiosError(err) &&
      err.response?.data instanceof Blob &&
      err.response.data.type === "application/json"
    ) {
      try {
        const errorJson = JSON.parse(
          await err.response.data.text()
        ) as ApiError;

        apiError = errorJson;
        errorMessage = apiError.message || errorMessage;
      } catch {
        // Failed to parse Blob error as JSON
      }
    } else if (isAxiosError(err) && err.response?.data) {
      try {
        if (
          typeof err.response.data === "object" &&
          !(err.response.data instanceof Blob)
        ) {
          apiError = err.response.data as ApiError;
          errorMessage = apiError.message || errorMessage;
        }
      } catch {
        // Error processing Axios error data
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});